import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Chip, IconButton, SxProps, TextField, Theme, Typography } from '@mui/material';
import { lengthRestrictions } from 'common/constants/length-restrictions';
import type { IPageFormV2 } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { DragEvent, FC, useEffect, useRef, useState } from 'react';
import {
  FieldArrayPath,
  FieldPath,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { styles } from './product-content-form.styles';

interface IFaqBlockProps {
  productIndex: number;
}

export const FaqBlock: FC<IFaqBlockProps> = ({ productIndex }) => {
  const {
    control,
    formState: { errors },
    getValues,
    register,
  } = useFormContext<IPageFormV2>();

  const LIMIT_Q = lengthRestrictions.product.content.faq.question;
  const LIMIT_A = lengthRestrictions.product.content.faq.answer;
  const MIN_VISIBLE = 3;

  const sectionRef = useRef<HTMLDivElement>(null);

  const faqPath = `products.${productIndex}.content.faq` as FieldArrayPath<IPageFormV2>;

  const faqItems = useFieldArray({ control, name: faqPath });

  const contentObj = useWatch({
    control,
    name: `products.${productIndex}.content` as const,
  });

  const watchedFaq = contentObj?.faq || [];

  const hasEmptyFaq = watchedFaq.some((faq) => !faq.question?.trim() || !faq.answer?.trim());

  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    const formValues = getValues();
    const initialFaq = formValues.products?.[productIndex]?.content?.faq || [];
    const hasInitialEmpty = initialFaq.some((faq) => !faq.question?.trim() || !faq.answer?.trim());
    return initialFaq.length > MIN_VISIBLE && hasInitialEmpty;
  });

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    if (faqItems.fields.length > MIN_VISIBLE && hasEmptyFaq) {
      setIsExpanded(true);
    }
  }, [faqItems.fields.length, hasEmptyFaq]);

  const handleAdd = () => {
    faqItems.append({ question: '', answer: '' });
    setIsExpanded(true);
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>, index: number) => {
    const formValues = getValues();
    const currentFaq = formValues.products?.[productIndex]?.content?.faq?.[index];

    if (!currentFaq?.question?.trim() || !currentFaq?.answer?.trim()) {
      e.preventDefault();
      return;
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.4';
    }, 0);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    const formValues = getValues();
    const targetFaq = formValues.products?.[productIndex]?.content?.faq?.[index];
    if (!targetFaq?.question?.trim() || !targetFaq?.answer?.trim()) return;

    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    const formValues = getValues();
    const targetFaq = formValues.products?.[productIndex]?.content?.faq?.[dropIndex];

    if (!targetFaq?.question?.trim() || !targetFaq?.answer?.trim()) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      faqItems.move(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e: DragEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleToggleExpand = () => {
    if (isExpanded && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsExpanded(!isExpanded);
  };

  const showOverlay = !isExpanded && faqItems.fields.length > MIN_VISIBLE;
  const visibleCount = isExpanded
    ? faqItems.fields.length
    : Math.min(faqItems.fields.length, MIN_VISIBLE);

  return (
    <Box ref={sectionRef} sx={{ scrollMarginTop: '120px' }}>
      <Box sx={styles.sectionTitle}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography fontWeight={600} variant="subtitle1">Frequently Asked Questions</Typography>
          {faqItems.fields.length > 0 && (
            <Chip color="primary" label={faqItems.fields.length} size="small" variant="outlined" />
          )}
        </Box>
        <Button
          disabled={hasEmptyFaq}
          onClick={handleAdd}
          size="small"
          startIcon={<AddOutlinedIcon />}
          variant="outlined"
        >
          Add FAQ
        </Button>
      </Box>
      {hasEmptyFaq && (
        <Typography color="error" sx={{ display: 'block', mb: 2 }} variant="caption">
          Please fill in the empty FAQ block or remove it using the trash icon.
        </Typography>
      )}
      {faqItems.fields.length === 0 && (
        <Box sx={styles.emptyState}>
          <HelpOutlineIcon sx={{ fontSize: 36 }} />
          <Typography variant="body2">No FAQ questions added yet</Typography>
        </Box>
      )}
      <Box sx={styles.gridWrapper}>
        <Box sx={styles.gridContainer}>
          {faqItems.fields.slice(0, visibleCount).map((elem, idx) => {
            const currentQ = watchedFaq[idx]?.question || '';
            const currentA = watchedFaq[idx]?.answer || '';
            const err = errors?.products?.[productIndex]?.content?.faq?.[idx];

            const isQError = !!err?.question || currentQ.length > LIMIT_Q;
            const isAError = !!err?.answer || currentA.length > LIMIT_A;
            const isDragOver = dragOverIndex === idx;

            const qPath =
              `products.${productIndex}.content.faq.${idx}.question` as FieldPath<IPageFormV2>;
            const aPath =
              `products.${productIndex}.content.faq.${idx}.answer` as FieldPath<IPageFormV2>;

            return (
              <Box
                key={elem.id}
                draggable
                onDragEnd={handleDragEnd}
                onDragEnter={(e) => handleDragEnter(e, idx)}
                onDragOver={(e) => e.preventDefault()}
                onDragStart={(e) => handleDragStart(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                sx={[styles.cardItem, isDragOver && styles.cardItemDragOver] as SxProps<Theme>}
              >
                <Box sx={styles.cardHeader}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={
                        [
                          styles.dragHandle,
                          {
                            cursor: !currentQ.trim() || !currentA.trim() ? 'not-allowed' : 'grab',
                            opacity: !currentQ.trim() || !currentA.trim() ? 0.3 : 1,
                          },
                        ] as SxProps<Theme>
                      }
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                    <Typography color="text.secondary" fontWeight="bold" variant="caption">
                      FAQ #{idx + 1}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => faqItems.remove(idx)}
                    size="small"
                    sx={styles.deleteAction}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={styles.cardItemContent}>
                  <TextField
                    {...register(qPath)}
                    error={isQError}
                    fullWidth
                    helperText={err?.question?.message}
                    placeholder="Question"
                    size="small"
                    slotProps={{ htmlInput: { maxLength: LIMIT_Q } }}
                  />
                  <Typography
                    sx={
                      [
                        styles.charCountSmall,
                        { color: currentQ.length > LIMIT_Q ? 'error.main' : 'text.secondary' },
                      ] as SxProps<Theme>
                    }
                  >
                    {currentQ.length}/{LIMIT_Q}
                  </Typography>
                </Box>

                <Box sx={styles.cardItemContent}>
                  <TextField
                    {...register(aPath)}
                    error={isAError}
                    fullWidth
                    helperText={err?.answer?.message}
                    multiline
                    placeholder="Answer"
                    rows={3}
                    size="small"
                    slotProps={{ htmlInput: { maxLength: LIMIT_A } }}
                    sx={
                      [
                        styles.multilineInput,
                        { '& .MuiInputBase-root': { height: '80px' } },
                      ] as SxProps<Theme>
                    }
                  />
                  <Typography
                    sx={
                      [
                        styles.charCountSmall,
                        { color: currentA.length > LIMIT_A ? 'error.main' : 'text.secondary' },
                      ] as SxProps<Theme>
                    }
                  >
                    {currentA.length}/{LIMIT_A}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        {showOverlay && <Box sx={styles.gradientOverlay} />}
      </Box>
      {faqItems.fields.length > MIN_VISIBLE && (
        <Box sx={styles.expandButtonWrapper}>
          <Button
            endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            onClick={handleToggleExpand}
            sx={styles.expandButton}
          >
            {isExpanded ? 'Show Less' : `Show All (${faqItems.fields.length})`}
          </Button>
        </Box>
      )}
    </Box>
  );
};
